import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/hooks/useAuth';
import { 
  FileText, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle, 
  CheckCircle,
  Clock,
  DollarSign,
  Activity,
  Zap,
  Building,
  Users,
  BarChart3,
  PieChart,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Eye,
  Plus,
  Settings,
  Bell,
  Target,
  Award,
  Lightbulb,
  Leaf,
  Cloud,
  Sun,
  CloudRain,
  Wind,
  Droplets,
  Thermometer,
  Gauge
} from 'lucide-react';

// Import enhanced components
import { ThemeToggle } from '@/components/ThemeToggle';
import { NotificationsPanel } from '@/components/NotificationsPanel';
import { EnergyEfficiencyGauge } from '@/components/EnergyEfficiencyGauge';
import { InteractiveDataViz } from '@/components/InteractiveDataViz';

// Utility Bill Management Dashboard
const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [timeOfDay, setTimeOfDay] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  useEffect(() => {
    const timer = setInterval(() => setTimeOfDay(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Real utility bill management data
  const billMetrics = {
    totalBills: 1247,
    processedToday: 23,
    pendingReview: 8,
    aiAccuracy: 98.5,
    monthlySavings: 15420,
    co2Reduction: 2.3,
    avgProcessingTime: 2.4,
    costPerBill: 0.85
  };

  const utilityTypes = [
    { name: 'Electricity', count: 456, trend: '+12%', color: 'text-yellow-500', icon: Zap },
    { name: 'Water', count: 234, trend: '+8%', color: 'text-blue-500', icon: Droplets },
    { name: 'Gas', count: 189, trend: '-3%', color: 'text-orange-500', icon: Thermometer },
    { name: 'Internet', count: 156, trend: '+15%', color: 'text-purple-500', icon: Cloud },
    { name: 'Waste', count: 98, trend: '+5%', color: 'text-green-500', icon: Leaf },
    { name: 'Other', count: 114, trend: '+2%', color: 'text-gray-500', icon: Building }
  ];

  const recentActivity = [
    { type: 'processed', message: 'Electricity bill from ABC Corp processed', time: '2 min ago', status: 'success' },
    { type: 'alert', message: 'High usage detected for XYZ Building', time: '15 min ago', status: 'warning' },
    { type: 'saved', message: '$2,340 saved through optimization recommendations', time: '1 hour ago', status: 'success' },
    { type: 'review', message: '3 bills require manual review', time: '2 hours ago', status: 'info' },
    { type: 'export', message: 'Monthly report exported successfully', time: '3 hours ago', status: 'success' }
  ];

  const topCustomers = [
    { name: 'ABC Corporation', bills: 45, amount: 125000, trend: '+8%' },
    { name: 'XYZ Industries', bills: 38, amount: 98000, trend: '+12%' },
    { name: 'Global Tech Ltd', bills: 32, amount: 87000, trend: '-2%' },
    { name: 'Metro Properties', bills: 28, amount: 76000, trend: '+15%' },
    { name: 'Green Energy Co', bills: 25, amount: 65000, trend: '+5%' }
  ];

  const efficiencyInsights = [
    { title: 'Peak Usage Optimization', description: 'Shift 15% of usage to off-peak hours', savings: 2300, impact: 'high' },
    { title: 'Energy Efficiency Upgrades', description: 'LED lighting retrofit recommended', savings: 1800, impact: 'medium' },
    { title: 'Water Conservation', description: 'Smart meters installation opportunity', savings: 1200, impact: 'medium' },
    { title: 'Renewable Energy', description: 'Solar panel feasibility study', savings: 4500, impact: 'high' }
  ];

  const getGreeting = () => {
    const hour = timeOfDay.getHours();
    // Prefer persisted nameOfUser in sessionStorage for immediate static display
    let nameOfUser: string | null = null;
    let username: string | null = null;
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('userData');
        if (raw) {
          const parsed = JSON.parse(raw);
            nameOfUser = parsed?.nameOfUser || null;
            username = parsed?.username || parsed?.email || null;
        }
      } catch (e) { /* ignore */ }
    }
    // Strict preference: nameOfUser -> user.name -> (email local-part) -> username/email -> 'User'
    const emailLocal = (username || user?.email || '').split('@')[0];
    const userName = nameOfUser || user?.name || emailLocal || username || user?.email || 'User';
    const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';
    return `${greeting}, ${userName}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section with Weather */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
            {getGreeting()}
          </h1>
          <p className="text-muted-foreground mt-2">
            Welcome to your comprehensive utility bill management and analytics platform
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            System Online
          </Badge>
          <div className="text-sm text-muted-foreground">
            {timeOfDay.toLocaleTimeString()}
          </div>
        </div>
      </div>


      {/* Key Metrics Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Bills Processed</CardTitle>
            <FileText className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{billMetrics.totalBills.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="inline w-3 h-3 mr-1 text-green-500" />
              +{billMetrics.processedToday} today
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Accuracy Rate</CardTitle>
            <Target className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{billMetrics.aiAccuracy}%</div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="inline w-3 h-3 mr-1 text-green-500" />
              +0.3% from last week
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Savings</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${billMetrics.monthlySavings.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="inline w-3 h-3 mr-1 text-green-500" />
              +12% from last month
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">CO₂ Reduction</CardTitle>
            <Leaf className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{billMetrics.co2Reduction} tons</div>
            <p className="text-xs text-muted-foreground">
              <TrendingDown className="inline w-3 h-3 mr-1 text-green-500" />
              -0.2 tons this month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Utility Types & Processing Status */}
        <div className="lg:col-span-2 space-y-6">
          {/* Utility Types Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5 text-blue-500" />
                Utility Types Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {utilityTypes.map((utility, index) => {
                  const IconComponent = utility.icon;
                  return (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <IconComponent className={`h-5 w-5 ${utility.color}`} />
                        <div>
                          <p className="font-medium">{utility.name}</p>
                          <p className="text-sm text-muted-foreground">{utility.count} bills</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className={utility.trend.startsWith('+') ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}>
                        {utility.trend}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Processing Status & Queue */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-green-500" />
                Processing Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Bills Processed Today</span>
                    <span className="text-2xl font-bold text-green-600">{billMetrics.processedToday}</span>
                  </div>
                  <Progress value={75} className="h-2" />
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Pending Review</span>
                    <span className="text-2xl font-bold text-yellow-600">{billMetrics.pendingReview}</span>
                  </div>
                  <Progress value={25} className="h-2" />
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Average Processing Time</span>
                    <span className="text-2xl font-bold text-blue-600">{billMetrics.avgProcessingTime}s</span>
                  </div>
                  <Progress value={85} className="h-2" />
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Cost Efficiency</h4>
                    <div className="text-3xl font-bold text-purple-600">${billMetrics.costPerBill}</div>
                    <p className="text-sm text-muted-foreground">Cost per bill processed</p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Environmental Impact</h4>
                    <div className="text-3xl font-bold text-green-600">{billMetrics.co2Reduction} tons</div>
                    <p className="text-sm text-muted-foreground">CO₂ reduced this month</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Customers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-purple-500" />
                Top Customers by Volume
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topCustomers.map((customer, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-sm text-muted-foreground">{customer.bills} bills • ${customer.amount.toLocaleString()}</p>
                      </div>
                  </div>
                    <Badge variant="secondary" className={customer.trend.startsWith('+') ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}>
                      {customer.trend}
                    </Badge>
                  </div>
                ))}
                </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Insights & Activity */}
        <div className="space-y-6">
          {/* Energy Efficiency Insights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Efficiency Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {efficiencyInsights.map((insight, index) => (
                  <div key={index} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-sm">{insight.title}</h4>
                      <Badge className={`text-xs ${getImpactColor(insight.impact)}`}>
                        {insight.impact}
                      </Badge>
      </div>
                    <p className="text-xs text-muted-foreground mb-2">{insight.description}</p>
              <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-600">${insight.savings.toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">potential savings</span>
                    </div>
                </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
        <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Recent Activity
              </CardTitle>
        </CardHeader>
        <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      activity.status === 'success' ? 'bg-green-500' :
                      activity.status === 'warning' ? 'bg-yellow-500' :
                      activity.status === 'error' ? 'bg-red-500' : 'bg-blue-500'
                    }`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{activity.message}</p>
                      <p className="text-xs text-muted-foreground">{activity.time}</p>
              </div>
            </div>
                ))}
              </div>
            </CardContent>
          </Card>


          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-green-500" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
            <div className="space-y-2">
                <Button className="w-full justify-start" variant="outline">
                  <FileText className="h-4 w-4 mr-2" />
                  Upload New Bills
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Generate Reports
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export Data
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Settings className="h-4 w-4 mr-2" />
                  System Settings
                </Button>
              </div>
            </CardContent>
          </Card>
            </div>
          </div>
    </div>
  );
};

export default Dashboard;